/**
 * RBAC 单一源 (V23, 2026-05-17)
 *
 * **权威源**: ../../../docs/contracts/v23/permissions.yaml
 * - `roles` 章节: 5 个角色 key
 * - `frontend_route_matrix` 章节: 路由 → 允许角色映射
 *
 * 本文件是手写常量(非 codegen),改动时**两边一起改**:
 *   1. 改 permissions.yaml(via proposal)
 *   2. 改本文件
 *
 * 不要在业务代码里硬编码 "PLATFORM_ADMIN" 等字面量,统一从这里 import:
 *   import { ROLES, type RoleKey, hasRole, isTenantManagerPlus } from "@/shared/lib/rbac";
 *
 * Session 2 epic-2 (2026-05-17) 删了 34 处硬编码字面量。
 */

/** 5 角色枚举,跟后端 JWT.role / V22 permission system 完全一致。 */
export const ROLES = {
  /** 平台超管 — 独立 chain, scope=PLATFORM, JWT 可带 actAsTenant 代客 */
  PLATFORM_ADMIN: "PLATFORM_ADMIN",
  /** 租户管理员 — 租户最高权, scope=TENANT */
  TENANT_ADMIN: "TENANT_ADMIN",
  /** 租户经理 — 中层管理, scope=TENANT, 数据权限可看全租户 */
  TENANT_MANAGER: "TENANT_MANAGER",
  /** 一线坐席 — scope=TENANT, 数据权限受限(只看自己的 PosterTask/CsSession/CoachingTask) */
  TENANT_STAFF: "TENANT_STAFF",
  /** 渠道终端(企微 H5 终端用户) — 独立 chain, scope=MEMBER */
  MEMBER: "MEMBER",
  /**
   * OEM 渠道商管理员(A-P2,0929)— 独立 chain, scope=PARTNER,token 带 partnerCode 无 tenantCode。
   * 只走 /api/v1/partner/** 与 /me/**;前端只放 /partner、/profile、/help、/403(ProtectedRoute)。
   * ★不进 ALL_ROLES / ADMIN_ROLES:那两张表给租户用户管理的下拉用,渠道管理员不在租户里建。
   */
  PARTNER_ADMIN: "PARTNER_ADMIN",
} as const;

export type RoleKey = (typeof ROLES)[keyof typeof ROLES];

/** 5 角色完整列表,UI 用作 dropdown options 等。 */
export const ALL_ROLES: readonly RoleKey[] = [
  ROLES.PLATFORM_ADMIN,
  ROLES.TENANT_ADMIN,
  ROLES.TENANT_MANAGER,
  ROLES.TENANT_STAFF,
  ROLES.MEMBER,
] as const;

/** 4 个后台角色(不含 MEMBER 渠道终端) */
export const ADMIN_ROLES: readonly RoleKey[] = [
  ROLES.PLATFORM_ADMIN,
  ROLES.TENANT_ADMIN,
  ROLES.TENANT_MANAGER,
  ROLES.TENANT_STAFF,
] as const;

/**
 * TENANT_MANAGER 及以上 —— 管理层特权门。
 *
 * 现状(经 `isTenantManagerPlus`):`OrgScopeFilterBar` / `useOrgScopeFilter` 的 `canSwitchScope`
 * (归属维度「全部 / 部门 / 成员 / 只看我的」四选;非管理层钳死在只看自己),以及获客线索分派、
 * 客服人工回复、知识库改可见范围 / 提议上传、军师存知识库等。
 * (旧注释写的「只看我的 toggle」是 0817 之前的二态开关,已被四维归属筛选替代。)
 */
export const TENANT_MANAGER_PLUS: readonly RoleKey[] = [
  ROLES.TENANT_MANAGER,
  ROLES.TENANT_ADMIN,
  ROLES.PLATFORM_ADMIN,
] as const;

/** TENANT_ADMIN 及以上(用于 billing / 用户管理等高权操作) */
export const TENANT_ADMIN_PLUS: readonly RoleKey[] = [
  ROLES.TENANT_ADMIN,
  ROLES.PLATFORM_ADMIN,
] as const;

/** 检查 role 是否在 allowed 列表里(undefined role 视为无权)。 */
export function hasRole(role: RoleKey | undefined | null, allowed: readonly RoleKey[]): boolean {
  if (!role) return false;
  return allowed.includes(role);
}

/** 是否管理层(MANAGER+):允许使用"只看我的"/全租户视图等。 */
export function isTenantManagerPlus(role: RoleKey | undefined | null): boolean {
  return hasRole(role, TENANT_MANAGER_PLUS);
}

/** 是否租户管理员级(ADMIN+):允许 billing / 用户管理 / WeCom 配置等。 */
export function isTenantAdminPlus(role: RoleKey | undefined | null): boolean {
  return hasRole(role, TENANT_ADMIN_PLUS);
}

/** OEM 渠道后台首页(渠道管理员登录后落这里)。 */
export const PARTNER_HOME = "/partner";

/**
 * 渠道管理员(PARTNER_ADMIN)前端可进的路由前缀(ProtectedRoute 白名单 + 登录落点判据同源)。
 * 渠道后台业务页全挂在 /partner/* 下;/profile 改密、/help 帮助、/403 越权落点。
 */
export const PARTNER_ADMIN_ROUTES: readonly string[] = [PARTNER_HOME, "/profile", "/help", "/403"];

/** 是否 OEM 渠道商管理员。 */
export function isPartnerAdmin(role: RoleKey | undefined | null): boolean {
  return role === ROLES.PARTNER_ADMIN;
}

/** 是否平台超管。 */
export function isPlatformAdmin(role: RoleKey | undefined | null): boolean {
  return role === ROLES.PLATFORM_ADMIN;
}

/** UI dropdown 角色 label(中文) — 跟 permissions.yaml.roles[].label 对齐。 */
export const ROLE_LABEL: Record<RoleKey, string> = {
  PLATFORM_ADMIN: "平台超管",
  TENANT_ADMIN: "租户管理员",
  TENANT_MANAGER: "租户经理",
  TENANT_STAFF: "租户员工",
  MEMBER: "渠道终端",
  PARTNER_ADMIN: "渠道管理员",
};
